// src/Features/Account/MfaManagementSection.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { Theme } from '../../Theme/Theme';
import { Button } from '../../Components/Button';

// --- Styled Components ---
const MfaCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const MfaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const MfaInfo = styled.div`
  flex: 1;
`;

const MfaTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MfaDescription = styled.p`
  font-size: 13px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const StatusBadge = styled.span<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => (props.$enabled ? '#dcfce7' : '#f3f4f6')};
  color: ${(props) => (props.$enabled ? '#166534' : '#6b7280')};
`;

const EnrollmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
`;

const EnrollmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 8px;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EnrollmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const EnrollmentIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.primary};
  font-size: 16px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const EnrollmentDetails = styled.div`
  min-width: 0;
`;

const EnrollmentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EnrollmentType = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px 16px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 8px;
  margin-top: 16px;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  color: ${Theme.Colors.warmGrey};
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 16px 0;
`;

const ErrorAlert = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #dc2626;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const RefreshButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  background: white;
  color: ${Theme.Colors.charcoal};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${Theme.Colors.lightestGrey};
    color: ${Theme.Colors.textColor};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
// ---

// --- MFA Enrollment Interface ---
interface MfaEnrollment {
  id: string;
  status: 'pending' | 'confirmed';
  type: 'guardian' | 'sms' | 'webauthn-roaming' | 'webauthn-platform' | string; // Allow for future types
  name?: string; // Name given by user (e.g., 'YubiKey', 'Work Phone')
  identifier?: string; // Often email for Guardian, potentially username for passkeys
  phoneNumber?: string; // For SMS MFA
  enrolledAt?: string; // ISO Date string
  lastAuth?: string; // ISO Date string
}
// ---

export const MfaManagementSection: React.FC = observer(() => {
  const { t } = useTrans();
  const [isLoading, setIsLoading] = useState(true); // For initial load
  const [isActionLoading, setIsActionLoading] = useState<string | false>(false); // 'enroll' or enrollment ID being removed
  const [error, setError] = useState<string | null>(null);
  const [enrollments, setEnrollments] = useState<MfaEnrollment[]>([]);
  const [wasEnrollmentInitiated, setWasEnrollmentInitiated] = useState(false); // Flag to track if enrollment was started

  // --- Fetch MFA Status ---
  const fetchMfaStatus = useCallback(
    async (showLoadingSpinner = true) => {
      if (showLoadingSpinner) setIsLoading(true);
      // Don't clear error on background refresh, only on explicit actions/initial load
      // setError(null);
      try {
        const fetchedEnrollments: MfaEnrollment[] = await HttpBackend.get('/uaa/mfa/status');
        console.log('MFA status fetched:', fetchedEnrollments);

        setEnrollments(fetchedEnrollments || []);
      } catch (err) {
        console.error('Error fetching MFA status:', err);
        if (showLoadingSpinner) {
          // Only show main error on initial load or manual refresh
          setError(t('profile.mfa.fetchStatusErrorMessage', { defaultValue: 'Could not load MFA status.' }));
          setEnrollments([]);
        }
      } finally {
        if (showLoadingSpinner) setIsLoading(false);
      }
    },
    [t],
  ); // Include dependencies

  // --- Effect for Visibility/Focus Change ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wasEnrollmentInitiated) {
        console.log('Tab became visible after MFA enrollment potentially initiated, refreshing status...');
        fetchMfaStatus(false); // Fetch status without showing main loading spinner
        // Flag is reset inside fetchMfaStatus if data changes
        setWasEnrollmentInitiated(false);
      }
    };

    const handleFocus = () => {
      if (wasEnrollmentInitiated) {
        console.log('Window focused after MFA enrollment potentially initiated, refreshing status...');
        fetchMfaStatus(false); // Fetch status without showing main loading spinner
        // Flag is reset inside fetchMfaStatus if data changes
        setWasEnrollmentInitiated(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchMfaStatus, wasEnrollmentInitiated]); // Dependency on the flag is crucial

  // --- Initial Fetch on Mount ---
  useEffect(() => {
    fetchMfaStatus();
  }, [fetchMfaStatus]); // Run only once on mount

  // --- Enroll & Unenroll Handlers ---
  const handleAddAuthenticator = useCallback(async () => {
    setIsActionLoading('enroll');
    setError(null);
    try {
      const data = await HttpBackend.post('/uaa/mfa/enroll');
      if (data?.ticket_url) {
        setWasEnrollmentInitiated(true); // Set flag *before* opening
        window.open(data.ticket_url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('No ticket_url received');
      }
    } catch (err) {
      console.error('Error initiating MFA enrollment:', err);
      setError(t('profile.mfa.enrollErrorMessage', { defaultValue: 'Could not start MFA setup. Please try again.' }));
      setWasEnrollmentInitiated(false); // Reset flag on error
    } finally {
      setIsActionLoading(false);
    }
  }, [t]);

  const handleRemoveAuthenticator = useCallback(
    async (enrollmentId: string) => {
      if (
        !window.confirm(
          t('profile.mfa.disableFactorConfirmMessage', {
            defaultValue: 'Are you sure you want to remove this authentication method?',
          }) as string,
        )
      ) {
        return;
      }
      setIsActionLoading(enrollmentId);
      setError(null);
      try {
        await HttpBackend.delete(`/uaa/mfa/unenroll/${enrollmentId}`);
        window.alert(
          t('profile.mfa.unenrollFactorSuccessMessage', { defaultValue: 'Authenticator removed successfully.' }),
        );
        await fetchMfaStatus(false); // Refresh list immediately
      } catch (err) {
        console.error('Error unenrolling MFA factor:', err);
        setError(
          t('profile.mfa.unenrollErrorMessage', { defaultValue: 'Could not remove authenticator. Please try again.' }),
        );
      } finally {
        setIsActionLoading(false);
      }
    },
    [t, fetchMfaStatus],
  );

  // --- Rendering Logic ---
  const confirmedEnrollments = enrollments.filter((e) => e.status === 'confirmed');
  const isMfaCurrentlyEnabled = confirmedEnrollments.length > 0;

  const getFactorTypeDisplay = (type: string): string => {
    if (type === 'sms') return t('profile.mfa.factorTypes.sms', { defaultValue: 'SMS' });
    if (type === 'guardian' || type === 'otp' || type === 'authenticator')
      return t('profile.mfa.factorTypes.otp', { defaultValue: 'Authenticator App' });
    if (type.includes('webauthn-roaming'))
      return t('profile.mfa.factorTypes.passkeyRoaming', { defaultValue: 'Security Key' });
    if (type.includes('webauthn-platform'))
      return t('profile.mfa.factorTypes.passkeyPlatform', { defaultValue: 'Device Passkey' });
    if (type.includes('webauthn'))
      return t('profile.mfa.factorTypes.passkey', { defaultValue: 'Passkey' });
    return t(`profile.mfa.factorTypes.${type}`, { defaultValue: type });
  };

  const getFactorIdentifier = (enrollment: MfaEnrollment): string => {
    if (enrollment.type === 'sms') return enrollment.phoneNumber || 'SMS';
    if (enrollment.type.includes('webauthn') && !enrollment.name) {
      return getFactorTypeDisplay(enrollment.type);
    }
    return (
      enrollment.name || enrollment.identifier || t('profile.mfa.unnamedDevice', { defaultValue: 'Unnamed Device' })
    );
  };

  const getFactorIcon = (type: string): string => {
    if (type === 'sms') return 'bi-phone';
    if (type === 'guardian' || type === 'otp' || type === 'authenticator') return 'bi-qr-code';
    if (type.includes('webauthn')) return 'bi-key';
    return 'bi-shield-check';
  };

  return (
    <MfaCard>
      <MfaHeader>
        <MfaInfo>
          <MfaTitle>
            {t('accountDetails.mfaLabel', { defaultValue: 'Multi-Factor Authentication' })}
            <StatusBadge $enabled={isMfaCurrentlyEnabled}>
              <i className={isMfaCurrentlyEnabled ? 'bi bi-check-circle-fill' : 'bi bi-circle'} />
              {isMfaCurrentlyEnabled
                ? t('profile.mfa.enabled', { defaultValue: 'Enabled' })
                : t('profile.mfa.disabled', { defaultValue: 'Disabled' })}
            </StatusBadge>
          </MfaTitle>
          <MfaDescription>
            {t('profile.mfa.description', {
              defaultValue: 'Add an extra layer of security to protect your account',
            })}
          </MfaDescription>
        </MfaInfo>
        <HeaderActions>
          <RefreshButton
            onClick={() => fetchMfaStatus()}
            disabled={isLoading || !!isActionLoading}
            title={t('globals.refresh', { defaultValue: 'Refresh' }) as string}
          >
            <i className="bi bi-arrow-clockwise" />
          </RefreshButton>
          <Button color="secondary" size="sm" onClick={handleAddAuthenticator} disabled={!!isActionLoading}>
            {isActionLoading === 'enroll' ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="bi bi-plus-lg" style={{ marginRight: 4 }} />
                {t('profile.mfa.addButton', { defaultValue: 'Add' })}
              </>
            )}
          </Button>
        </HeaderActions>
      </MfaHeader>

      {isLoading && !isActionLoading && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}

      {error && (
        <ErrorAlert>
          <i className="bi bi-exclamation-circle" />
          {error}
        </ErrorAlert>
      )}

      {!isLoading && !error && (
        <>
          {confirmedEnrollments.length > 0 ? (
            <EnrollmentsList>
              {confirmedEnrollments.map((enrollment) => (
                <EnrollmentItem key={enrollment.id}>
                  <EnrollmentInfo>
                    <EnrollmentIcon>
                      <i className={`bi ${getFactorIcon(enrollment.type)}`} />
                    </EnrollmentIcon>
                    <EnrollmentDetails>
                      <EnrollmentName>{getFactorIdentifier(enrollment)}</EnrollmentName>
                      <EnrollmentType>{getFactorTypeDisplay(enrollment.type)}</EnrollmentType>
                    </EnrollmentDetails>
                  </EnrollmentInfo>
                  <RemoveButton
                    onClick={() => handleRemoveAuthenticator(enrollment.id)}
                    disabled={isActionLoading === enrollment.id}
                    title={t('profile.mfa.removeButtonTooltip', { defaultValue: 'Remove this method' }) as string}
                  >
                    {isActionLoading === enrollment.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="bi bi-trash3" />
                        {t('globals.remove', { defaultValue: 'Remove' })}
                      </>
                    )}
                  </RemoveButton>
                </EnrollmentItem>
              ))}
            </EnrollmentsList>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <i className="bi bi-shield" />
              </EmptyIcon>
              <EmptyText>
                {t('profile.mfa.noMethodsEnrolled', {
                  defaultValue: 'No authentication methods set up yet. Add one to secure your account.',
                })}
              </EmptyText>
              <Button color="primary" size="sm" onClick={handleAddAuthenticator} disabled={!!isActionLoading}>
                {isActionLoading === 'enroll' ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />
                    {t('profile.mfa.addAuthenticatorButton', { defaultValue: 'Add Authenticator' })}
                  </>
                )}
              </Button>
            </EmptyState>
          )}
        </>
      )}
    </MfaCard>
  );
});
